<template>
  <v-dialog
    :value="value"
    max-width="700px"
    persistent
    @input="onDialogInput"
  >
    <v-card>
      <v-card-title>
        <span class="headline">New Machine Token</span>
      </v-card-title>

      <v-card-text>
        <template v-if="!mintedToken">
          <p class="body-2">
            One token per workstation. Give it only the scopes that desk needs: a token can do
            nothing beyond them, and it cannot be widened later.
          </p>

          <v-text-field
            id="machineTokenLabel"
            v-model="label"
            label="Label (which machine is this?)"
            hint="e.g. Washington - front desk PC. This is what you will pick from when pausing or revoking."
            persistent-hint
            dense
          ></v-text-field>

          <v-select
            id="machineTokenScopes"
            v-model="scopes"
            :items="availableScopes"
            label="Scopes"
            multiple
            chips
            dense
            class="mt-4"
          ></v-select>

          <v-text-field
            id="machineTokenWkst"
            v-model="wkst"
            label="Workstation # (optional)"
            hint="Recorded on the token and stamped into its sign-in, so a push is attributable to a desk."
            persistent-hint
            type="number"
            min="1"
            dense
            class="mt-4"
          ></v-text-field>
        </template>

        <v-alert
          v-else
          type="warning"
          outlined
          dense
        >
          <div class="font-weight-bold mb-2">
            Copy this token now &mdash; it cannot be shown again.
          </div>
          <v-text-field
            id="machineTokenMintedValue"
            :value="mintedToken"
            readonly
            dense
            hide-details
            class="mb-2"
          ></v-text-field>
          <v-btn small color="primary" @click="copyMinted">Copy</v-btn>
          <span v-if="copied" class="ml-2 caption">Copied</span>
          <div class="caption mt-2">
            Paste it into that workstation's Settings &rarr; Push to Cloud &rarr; Machine Token.
            If it is lost, revoke this token and create another.
          </div>
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="blue darken-1" @click="close">
          {{ mintedToken ? 'Done' : 'Cancel' }}
        </v-btn>
        <v-btn
          v-if="!mintedToken"
          id="machineTokenCreateBtn"
          text
          color="blue darken-1"
          :disabled="!canCreate"
          @click="create"
        >
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Mint one machine token.
 *
 * The printed token is returned exactly once, by the mutation. There is no read-back path on the
 * server, so it lives in this component's data, is never committed to Vuex (which every component
 * can read and which survives navigation) and is dropped when the dialog closes. `persistent` on
 * the dialog is deliberate: a click outside must not discard a token that cannot be recovered.
 *
 * A label and at least one scope are required, matching the server: an unlabelled token is one
 * nobody can safely revoke, and a token with no scopes can do nothing.
 */
export default {
  name: 'MachineTokenMintDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    label: '',
    scopes: [],
    wkst: '',
    mintedToken: '',
    copied: false,
  }),
  computed: {
    // Built from the server's list, so a scope added in tmc-local-auth appears here without a
    // frontend release.
    availableScopes() {
      return this.$store.state.UserAdministration.machineTokenScopes || [];
    },
    canCreate() {
      return !!String(this.label).trim() && this.scopes.length > 0;
    },
  },
  methods: {
    async create() {
      if (!this.canCreate) return;
      const wkstNum = parseInt(this.wkst, 10);
      const minted = await this.$store.dispatch('UserAdministration/createMachineToken', {
        label: String(this.label).trim(),
        scopes: this.scopes,
        wkst: Number.isNaN(wkstNum) ? undefined : wkstNum,
      });
      if (minted && minted.token) {
        this.mintedToken = minted.token;
        this.copied = false;
      }
    },
    copyMinted() {
      // navigator.clipboard is absent on http origins and in older embedded browsers; the value
      // stays selectable in the field either way.
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.mintedToken);
        this.copied = true;
      }
    },
    onDialogInput(isOpen) {
      if (!isOpen) this.close();
    },
    close() {
      this.label = '';
      this.scopes = [];
      this.wkst = '';
      this.mintedToken = '';
      this.copied = false;
      this.$emit('input', false);
    },
  },
};
</script>
